import gql from "graphql-tag";
import { useEffect, useRef, useState } from "react";
import { CarouselPagination, Container, HeadlineAnim, RevealWrapper } from "@components";
import { Splide, SplideTrack } from "react-splide-ts";
import { CarouselShopFragment } from "@graphqlTypes";
import { getHighlightMarkup } from "@/core/utils";
import { useInView } from "motion/react";
import { useViewSize } from "@/providers/ViewSizeContext";

import { AnimVals } from "@/utils/animUtils";
import { useDebug } from "@/utils/useDebugLog";

import ShopCarouselItem from "./CarouselShopItem";

import className from "classnames/bind";
import styles from "./CarouselShop.module.scss";
const cx = className.bind(styles);

type CarouselShopProps = Omit<CarouselShopFragment, '__typename'> & {
  externalProducts?: ShopifyCarouselProduct[];
  noSearch?: boolean;
  carouselCtaIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
};

export default function CarouselShop(props: CarouselShopProps) {
  const { type, shopifyCollection, externalProducts, searchValue, shopifyTag, headline, cta, noSearch, carouselCtaIcon, colorOverride } = props;
  const carouselType = type ? type[0] : undefined;
  const collection = shopifyCollection ? shopifyCollection[0] : "";
  const tag = shopifyTag ? shopifyTag[0] : "";
  const carouselCta = { ...cta, target: '_blank' };
  const headlineMarkup = getHighlightMarkup(headline);
  const highlightColor = colorOverride?.[0] || "";
  const [style, setStyle] = useState<React.CSSProperties>({});

  const rootEl = useRef<HTMLDivElement>(null);

  const { log } = useDebug({ name: 'CarouselShop', bgColor: 'blue', textColor: 'white', enabled: false });

  // -------------------- Products (fetch data)

  const [products, setProducts] = useState<ShopifyCarouselProduct[]>(externalProducts || []);
  const isProductsLoading = products.length === 0;

  const fetchData = async () => {
    let value = collection;
    if (carouselType === 'tag') value = tag;
    if (carouselType === 'search') value = encodeURIComponent(searchValue);
    const data = await fetch(`/api/products/${carouselType}/${value}/`);
    if (data.ok) {
      const products = await data.json();
      setProducts(products.slice(0, 10));
    }
  };

  useEffect(() => {
    if (!noSearch) {
      fetchData();
    }
  }, [carouselType, collection]);

  useEffect(() => {
    if (highlightColor) {
      setStyle({ "--highlight-color": highlightColor } as React.CSSProperties);
    }
  }, [highlightColor]);

  // -------------------- Splide

  const splideOptions = {
    type: 'slide',
    arrows: true,
    pagination: true,
    speed: 500,
    flickMaxPages: 1,
    flickPower: 250,
    updateOnMove: true,

    // Desktop first (Splide default)
    perPage: 4,
    perMove: 4,
    gap: '3rem',
    padding: {
      left: '5rem',
      right: '5rem',
    },

    // Responsive options (smaller than desktop)
    breakpoints: {
      // Below 1025px
      1025: {
        perPage: 2,
        perMove: 2,
        fixedWidth: '36%',
        gap: '3rem',
        padding: {
          left: '4rem',
          right: '4rem',
        },
        arrows: false
      },
      // Below 769px
      769: {
        perPage: 1,
        perMove: 1,
        fixedWidth: '76%',
        gap: '2rem',
        padding: {
          left: '2rem',
          right: '2rem',
        },
      },
    }
  };

  // Override accessibility attributes
  const onSplideMount = () => {
    if (!rootEl.current) return;
    const lists = rootEl.current.querySelectorAll('.splide__list');
    lists.forEach(list => list.setAttribute('role', 'list'));
    const slides = rootEl.current.querySelectorAll('.splide__slide');
    slides.forEach(slide => slide.setAttribute('role', 'listitem'));
  };

  // -------------------- Animation

  const { isDesktop, isMobile } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  const visibleProducts = isMobile ? products.slice(0, 5) : products;

  // -------------------- Render

  return (
    <section className={cx(["carousel-shop"])} ref={rootEl} style={style}>
      <RevealWrapper isInView={isInView}>
        { headlineMarkup && (
          <Container>
            <HeadlineAnim
              tag="h2"
              text={headlineMarkup}
              className={cx(["title-uc-l", "carousel-shop__headline"])}
              isRevealEnabled={isRevealEnabled}
              isInView={isInView}
            />
          </Container>
        )}

        <div className={cx("splide-container")}>
          {isProductsLoading ? (
            log('Products loading'),

            <Container>
              <span className="eyebrow-l">Products loading</span>
            </Container>

          ) : products.length === 0 ? (
            log('Products done loading, No products found'),

            <Container>
              <span className="eyebrow-l">No products found</span>
            </Container>

          ) : (
            log('Products done loading, Products found'),

            <Splide
              className={cx("carousel-shop__carousel")}
              hasTrack={false}
              options={splideOptions}
              onMounted={onSplideMount}
            >
              <SplideTrack>
                {visibleProducts?.map((product, index) => (
                  <ShopCarouselItem {...product} key={index} />
                ))}
              </SplideTrack>

              <Container>
                <CarouselPagination
                  className={cx("carousel-shop__pagination")}
                  cta={carouselCta}
                  ctaIcon={carouselCtaIcon}
                />
              </Container>
            </Splide>
          )}
        </div>
      </RevealWrapper>
    </section>
  );

}

CarouselShop.fragments = {
  entry: gql`
    fragment CarouselShop on FlexiblePageContentContentCarouselShopLayout {
      headline
      type
      colorOverride
      shopifyCollection
      shopifyTag
      searchValue
      cta {
        linkType
        label
        externalLink
        page {
          node {
            uri
          }
        }
      }
    }
  `
};
